import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    MdPersonAdd, MdListAlt, MdCheckCircle, MdAddCircle, MdError,
    MdCurrencyRupee, MdGroup, MdRefresh, MdSearch, MdPerson,
    MdClass, MdChevronLeft, MdChevronRight,
    MdArrowForward, MdClose, MdLock, MdSchool, MdCalendarToday, MdFingerprint,
    MdToday, MdPhone, MdWarning, MdEmail, MdAccessTime, MdInfo, MdFileDownloadDone, MdLocationOn
} from 'react-icons/md';
import api from '../api/axios';
import { useAuth } from '../contexts/AuthContext';

const cardRadius = '12px';

// ── Reusable table components ──
const Th = ({ children }) => (
    <th style={{
        padding: '0.85rem 1rem', textAlign: 'left', fontSize: '0.75rem',
        fontWeight: '700', color: '#718096', textTransform: 'uppercase',
        letterSpacing: '0.05em', background: '#F8FAFC', whiteSpace: 'nowrap',
        borderBottom: '1px solid #E2E8F0'
    }}>{children}</th>
);
const Td = ({ children, style, onClick }) => (
    <td onClick={onClick} style={{
        padding: '0.85rem 1rem', fontSize: '0.88rem', color: '#4A5568',
        borderBottom: '1px solid #F7FAFC', cursor: onClick ? 'pointer' : 'default', ...style
    }}>{children}</td>
);

function FormInput({ label, name, type = 'text', value, onChange, required, placeholder, readOnly }) {
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
            <label style={{ fontSize: '0.85rem', fontWeight: '600', color: '#4A5568' }}>{label}</label>
            <input
                type={type} name={name} value={value} onChange={onChange}
                required={required} placeholder={placeholder} readOnly={readOnly}
                style={{
                    padding: '0.8rem', borderRadius: '4px', border: '1px solid #CBD5E0',
                    width: '100%', outline: 'none', fontSize: '0.9rem', boxSizing: 'border-box',
                    background: readOnly ? '#F7FAFC' : '#FFFFFF',
                    color: readOnly ? '#718096' : '#2D3748',
                    cursor: readOnly ? 'not-allowed' : 'text'
                }}
            />
        </div>
    );
}

export default function DrccApprovalPage() {
    const { user } = useAuth();
    const [activeView, setActiveView] = useState('list'); // 'list' or 'form'
    const [approvals, setApprovals] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fetchLoading, setFetchLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [msg, setMsg] = useState('');
    const [submitLoading, setSubmitLoading] = useState(false);

    const [formData, setFormData] = useState({
        student_name: '',
        father_name: '',
        aadhar_no: '',
        drcc_no: '',
        mobile_no: '',
        address: ''
    });
    const [statusUpdating, setStatusUpdating] = useState(null);

    const fetchApprovals = async () => {
        setFetchLoading(true);
        try {
            const res = await api.get('/drcc-approvals');
            setApprovals(res.data);
        } catch (error) {
            console.error("Failed to fetch DRCC approvals");
        } finally {
            setFetchLoading(false);
        }
    };

    useEffect(() => {
        fetchApprovals();
    }, []);

    const handleInputChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitLoading(true);
        setMsg('');
        try {
            await api.post('/drcc-approvals', formData);
            setMsg('DRCC Approval request submitted successfully!');
            setFormData({
                student_name: '',
                father_name: '',
                aadhar_no: '',
                drcc_no: '',
                mobile_no: '',
                address: ''
            });
            fetchApprovals();
            setActiveView('list');
        } catch (error) {
            setMsg(error.response?.data?.message || 'Submission failed');
        } finally {
            setSubmitLoading(false);
        }
    };

    const handleStatusUpdate = async (id, newStatus) => {
        setStatusUpdating(id);
        try {
            await api.put(`/drcc-approvals/${id}/status`, { status: newStatus });
            setApprovals(prev => prev.map(a => a.id === id ? { ...a, status: newStatus } : a));
        } catch (error) {
            alert('Status update failed');
        } finally {
            setStatusUpdating(null);
        }
    };

    const filtered = approvals.filter(a =>
        a.student_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        a.aadhar_no.includes(searchTerm) ||
        (a.drcc_no && a.drcc_no.includes(searchTerm))
    );

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', marginBottom: '2rem' }} className="fade-in">
            {/* Header / Stats */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.8rem', paddingBottom: '0.5rem' }}>
                <div style={{ flex: '1' }}>
                    <h2 style={{ fontSize: '1.2rem', fontWeight: '800', color: '#1A202C', margin: 0 }}>DRCC Approvals</h2>
                </div>
                <div>
                    <button
                        onClick={() => setActiveView(activeView === 'list' ? 'form' : 'list')}
                        style={{
                            padding: '0.5rem 1rem', background: activeView === 'list' ? '#3182CE' : '#718096',
                            color: 'white', border: 'none', borderRadius: '6px', fontWeight: '700',
                            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.4rem', boxShadow: '0 4px 6px rgba(49, 130, 206, 0.2)',
                            fontSize: '0.85rem'
                        }}
                    >
                        {activeView === 'list' ? <><MdPersonAdd size={16} /> New Request</> : <><MdListAlt size={16} /> View List</>}
                    </button>
                </div>
            </div>

            {activeView === 'form' ? (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} style={{ background: 'white', padding: '2rem', borderRadius: cardRadius, border: '1px solid #E2E8F0', boxShadow: '0 4px 6px rgba(0,0,0,0.02)' }}>
                    <h3 style={{ margin: '0 0 1.5rem', fontSize: '1.1rem', fontWeight: '700', color: '#2D3748', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <MdPersonAdd color="#3182CE" /> Student Details for DRCC
                    </h3>
                    <form onSubmit={handleSubmit} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem' }}>
                        <FormInput label="Student Name" name="student_name" value={formData.student_name} onChange={handleInputChange} required placeholder="Full Name of Student" />
                        <FormInput label="Father's Name" name="father_name" value={formData.father_name} onChange={handleInputChange} required placeholder="Father's Full Name" />
                        <FormInput label="Aadhar No." name="aadhar_no" value={formData.aadhar_no} onChange={handleInputChange} required placeholder="12 Digit Aadhar Number" />
                        <FormInput label="DRCC No. (If any)" name="drcc_no" value={formData.drcc_no} onChange={handleInputChange} placeholder="DRCC Registration No." />
                        <FormInput label="Mobile No." name="mobile_no" value={formData.mobile_no} onChange={handleInputChange} required placeholder="10 Digit Mobile No." />
                        <div style={{ gridColumn: '1 / -1' }}>
                            <label style={{ fontSize: '0.85rem', fontWeight: '600', color: '#4A5568', display: 'block', marginBottom: '0.4rem' }}>Physical Address</label>
                            <textarea
                                name="address" value={formData.address} onChange={handleInputChange} required
                                style={{ width: '100%', padding: '0.8rem', borderRadius: '4px', border: '1px solid #CBD5E0', outline: 'none', minHeight: '80px', fontSize: '0.9rem' }}
                                placeholder="Complete address of student"
                            />
                        </div>
                        <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'center', gap: '1rem', marginTop: '1rem', flexWrap: 'wrap' }}>
                            {msg && <span style={{ color: msg.includes('success') ? '#2F855A' : '#C53030', alignSelf: 'center', fontSize: '0.9rem', fontWeight: '600', width: '100%', textAlign: 'center' }}>{msg}</span>}
                            <button type="submit" disabled={submitLoading} style={{ padding: '0.8rem 2.5rem', background: '#3182CE', color: 'white', border: 'none', borderRadius: '8px', fontWeight: '700', cursor: submitLoading ? 'not-allowed' : 'pointer', opacity: submitLoading ? 0.7 : 1, minWidth: '200px' }}>
                                {submitLoading ? 'Submitting...' : 'Submit Request'}
                            </button>
                        </div>
                    </form>
                </motion.div>
            ) : (
                <div style={{ background: 'white', borderRadius: cardRadius, border: '1px solid #E2E8F0', overflow: 'hidden', boxShadow: '0 4px 6px rgba(0,0,0,0.02)' }}>
                    <div style={{ padding: '0.8rem 1rem', background: '#F8FAFC', borderBottom: '1px solid #E2E8F0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.8rem', flexWrap: 'wrap' }}>
                        <div style={{ position: 'relative', flex: 1, maxWidth: '350px' }}>
                            <MdSearch style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#A0AEC0' }} />
                            <input
                                placeholder="Search student name, aadhar..."
                                value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                                style={{ width: '100%', padding: '0.5rem 0.5rem 0.5rem 2.22rem', borderRadius: '6px', border: '1px solid #E2E8F0', outline: 'none', fontSize: '0.8rem', background: 'white' }}
                            />
                        </div>
                        <span style={{ fontSize: '0.8rem', color: '#4A5568', fontWeight: '700', background: '#EBF8FF', padding: '0.3rem 0.7rem', borderRadius: '6px', border: '1px solid #BEE3F8' }}>Records: {filtered.length}</span>
                    </div>

                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr>
                                    <Th>S.N</Th>
                                    <Th>Student Name</Th>
                                    <Th>Father's Name</Th>
                                    <Th>Aadhar No</Th>
                                    <Th>DRCC No</Th>
                                    <Th>Mobile</Th>
                                    <Th>Status</Th>
                                    <Th>Date</Th>
                                </tr>
                            </thead>
                            <tbody>
                                {fetchLoading ? (
                                    <tr><Td colSpan="8" style={{ textAlign: 'center', padding: '3rem', color: '#A0AEC0' }}>Loading data...</Td></tr>
                                ) : filtered.length === 0 ? (
                                    <tr><Td colSpan="8" style={{ textAlign: 'center', padding: '3rem', color: '#A0AEC0' }}>No DRCC records found.</Td></tr>
                                ) : (
                                    filtered.map((item, idx) => (
                                        <tr key={item.id}>
                                            <Td>{idx + 1}</Td>
                                            <Td style={{ fontWeight: '700', color: '#2D3748' }}>{item.student_name}</Td>
                                            <Td>{item.father_name}</Td>
                                            <Td>{item.aadhar_no}</Td>
                                            <Td>{item.drcc_no || '—'}</Td>
                                            <Td>{item.mobile_no}</Td>
                                            <Td>
                                                {statusUpdating === item.id ? (
                                                    <span style={{ fontSize: '0.65rem', color: '#A0AEC0' }}>Updating...</span>
                                                ) : (
                                                    <select
                                                        value={item.status}
                                                        onChange={e => handleStatusUpdate(item.id, e.target.value)}
                                                        style={{
                                                            padding: '2px 8px', borderRadius: '4px', border: '1px solid #E2E8F0', fontSize: '0.72rem', fontWeight: '800',
                                                            background: item.status === 'Approved' ? '#C6F6D5' : item.status === 'Rejected' ? '#FED7D7' : '#FEFCBF',
                                                            color: item.status === 'Approved' ? '#22543D' : item.status === 'Rejected' ? '#822727' : '#744210',
                                                            outline: 'none', cursor: 'pointer', textTransform: 'uppercase'
                                                        }}
                                                    >
                                                        <option value="Pending">Pending</option>
                                                        <option value="Approved">Approved</option>
                                                        <option value="Rejected">Rejected</option>
                                                    </select>
                                                )}
                                            </Td>
                                            <Td>{new Date(item.created_at).toLocaleDateString()}</Td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}
